import type { WebGLRenderer } from 'three';
import type { TerrainStats } from '../world/Terrain';

interface HudInput {
  renderer: WebGLRenderer;
  terrain: TerrainStats;
  renderScale: number;
  cpuMs: number;
  position: { x: number; y: number; z: number };
  walkMode: boolean;
  grassQuality: number | null;
}

/**
 * Overlay se statistikami.
 *
 * Není to kosmetika — bez viditelných draw callů, trojúhelníků a render scale
 * se výkonová regrese pozná až když je pozdě. Aktualizuje se 5x za sekundu,
 * ne každý snímek, aby samotný HUD nezkresloval měření.
 */
export class Hud {
  private readonly el: HTMLElement;
  private readonly helpEl: HTMLElement;
  private frames = 0;
  private accum = 0;
  private cpuAccum = 0;
  private fps = 0;
  private worstFrame = 0;
  private worstShown = 0;
  private visible = true;

  constructor() {
    this.el = document.getElementById('hud') as HTMLElement;
    this.helpEl = document.getElementById('help') as HTMLElement;
    this.helpEl.textContent = [
      'klik  ovládání myší      WASD  pohyb',
      'Space/C  nahoru/dolů     Shift  sprint',
      'G  chůze / let           H  skrýt HUD',
    ].join('\n');

    window.addEventListener('keydown', (e) => {
      if (e.code !== 'KeyH') return;
      this.visible = !this.visible;
      this.el.style.display = this.visible ? '' : 'none';
      this.helpEl.style.display = this.visible ? '' : 'none';
    });
  }

  update(dt: number, input: HudInput): void {
    this.frames++;
    this.accum += dt;
    this.cpuAccum += input.cpuMs;
    if (dt > this.worstFrame) this.worstFrame = dt;

    if (this.accum < 0.2) return;

    this.fps = this.frames / this.accum;
    const cpuMs = this.cpuAccum / this.frames;
    this.worstShown = this.worstFrame * 1000;
    this.frames = 0;
    this.accum = 0;
    this.cpuAccum = 0;
    this.worstFrame = 0;

    if (!this.visible) return;

    const info = input.renderer.info;
    const cls = this.fps >= 55 ? 'g' : this.fps >= 30 ? 'y' : 'r';
    const lod = input.terrain.lodHistogram.map((n, i) => `L${i}:${n}`).join(' ');
    const p = input.position;

    this.el.innerHTML =
      `<b>FPS</b> <span class="${cls}">${this.fps.toFixed(0)}</span>` +
      `  worst ${this.worstShown.toFixed(1)} ms\n` +
      `<b>CPU</b> ${cpuMs.toFixed(2)} ms   <b>scale</b> ${(input.renderScale * 100).toFixed(0)}%\n` +
      `<b>draw</b> ${info.render.calls}   <b>tris</b> ${formatCount(info.render.triangles)}\n` +
      `<b>chunky</b> ${input.terrain.chunksVisible}/${input.terrain.chunksTotal}   <b>terén</b> ${formatCount(input.terrain.triangles)}\n` +
      `<b>lod</b> ${lod}\n` +
      `<b>geom</b> ${info.memory.geometries}  <b>tex</b> ${info.memory.textures}  <b>prog</b> ${info.programs?.length ?? 0}\n` +
      `<b>tráva</b> ${input.grassQuality === null ? 'vyp' : `${(input.grassQuality * 100).toFixed(0)} %`}
` +
      `<b>xyz</b> ${p.x.toFixed(0)} ${p.y.toFixed(0)} ${p.z.toFixed(0)}  ${input.walkMode ? 'chůze' : 'let'}`;
  }
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}k`;
  return String(n);
}
